import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { collection, addDoc, serverTimestamp, deleteDoc, doc, query, where, onSnapshot, getDocs, updateDoc, setDoc } from "firebase/firestore";
import { storage, db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { generateAttachmentId } from './documentIdUtils';

/**
 * Uploads a file to Firebase Storage and creates or overwrites a Firestore record.
 * If a file with the same name exists for the same entity, it will be overwritten.
 * @param {Object} params
 * @param {File} file
 * @param {string} entityId
 * @param {string} entityType - e.g. 'customer', 'opportunity', 'user'
 * @param {Object} currentUser
 * @returns {Promise<void>}
 */
export async function uploadAttachment({ file, entityId, entityType, currentUser }) {
    const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
    ];
    if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Allowed: PDF, DOCX, XLSX, CSV.');
    }

    // Check for existing attachment with same name for this entity
    const attachmentsRef = collection(db, collectionPath('attachments'));
    const q = query(
        attachmentsRef,
        where('entityId', '==', entityId),
        where('entityType', '==', entityType),
        where('name', '==', file.name)
    );
    const existing = await getDocs(q);

    let storagePath;
    let docId;

    if (!existing.empty) {
        // Overwrite: delete old file from storage, reuse doc
        const existingDoc = existing.docs[0];
        const oldData = existingDoc.data();
        docId = existingDoc.id;
        storagePath = oldData.storagePath;

        // Delete old file in storage
        if (storagePath) {
            try {
                await deleteObject(ref(storage, storagePath));
            } catch (e) {
                // File may not exist, ignore
            }
        }
    } else {
        // New file path and doc
        storagePath = `attachments/${entityType}/${entityId}/${file.name}`;
    }

    // Upload new file (overwrites if same path)
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    // Prepare attachment data
    const attachmentData = {
        entityId,
        entityType,
        url,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: serverTimestamp(),
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || null,
        storagePath: storageRef.fullPath
    };

    if (docId) {
        // Update existing Firestore doc
        await updateDoc(doc(db, collectionPath('attachments', docId)), attachmentData);
    } else {
        // Create new Firestore doc with human-readable ID
        const attachmentId = generateAttachmentId(entityType, entityId, file.name);
        await setDoc(doc(db, collectionPath('attachments'), attachmentId), attachmentData);
    }
}

/**
 * Deletes a file from Firebase Storage and removes its Firestore record.
 * @param {Object} attachment - The attachment object from Firestore.
 * @returns {Promise<void>}
 */
export async function deleteAttachment(attachment) {
    if (attachment.storagePath) {
        const fileRef = ref(storage, attachment.storagePath);
        await deleteObject(fileRef);
    }
    if (attachment.id) {
        await deleteDoc(doc(db, collectionPath('attachments', attachment.id)));
    }
}

/**
 * Subscribe to attachments for an entity.
 * @param {string} entityId
 * @param {string} entityType
 * @param {function} callback
 * @returns {function} unsubscribe
 */
export function subscribeToAttachments(entityId, entityType, callback) {
    const q = query(
        collection(db, collectionPath('attachments')),
        where('entityId', '==', entityId),
        where('entityType', '==', entityType)
    );
    return onSnapshot(q, (snap) => {
        callback(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
}

/**
 * Checks if an attachment exists for the given entity and file name.
 * @param {Object} params
 * @param {string} fileName
 * @param {string} entityId
 * @param {string} entityType
 * @returns {Promise<boolean>}
 */
export async function checkAttachmentExists({ fileName, entityId, entityType }) {
    const attachmentsRef = collection(db, collectionPath('attachments'));
    const q = query(
        attachmentsRef,
        where('entityId', '==', entityId),
        where('entityType', '==', entityType),
        where('name', '==', fileName)
    );
    const existing = await getDocs(q);
    return !existing.empty;
}
